/**
 * The panel's OTP login flow, as client state (US-01-005).
 *
 * DELIBERATELY THINNER THAN THE USER APP'S EQUIVALENT, and the difference is a
 * security control rather than a simplification:
 *
 *   - there is NO `isRegistered`. The user app reveals whether a number is known
 *     because it has to decide whether to show the profile fields; the panel must
 *     not, because an unknown number and a deactivated Expert are answered
 *     identically with `not_authorised` and no message of any kind is sent
 *     (US-01-005.6/.7). A field here would be a field a screen could render.
 *   - there is NO registration draft. The panel has no self-sign-up route at all
 *     (US-01-005.13); Experts are provisioned by a Super Admin.
 *
 * As in the user app, the failed-attempt count is the server's (US-01-005.2) and
 * the resend cooldown comes from `challenge.resendAvailableAt` (US-01-005.10).
 */

import { type PanelOtpChallenge } from "@anthra/platform/domain"
import { create } from "zustand"

export type PanelEntryPhase = "number" | "code"

type Lockout = {
	message: string
	retryAfterSeconds: number | undefined
}

type PanelEntryState = {
	mobileNumber: string
	challenge: PanelOtpChallenge | null
	lockout: Lockout | null

	setMobileNumber: (value: string) => void
	codeSent: (challenge: PanelOtpChallenge) => void
	recordLockout: (lockout: Lockout) => void
	reset: () => void
}

export const usePanelEntryStore = create<PanelEntryState>()(set => ({
	mobileNumber: "",
	challenge: null,
	lockout: null,

	setMobileNumber: value => {
		// A challenge issued for the previous number must not be verifiable
		// against this one.
		set({ mobileNumber: value, challenge: null, lockout: null })
	},

	codeSent: challenge => {
		set({ challenge, lockout: null })
	},

	recordLockout: lockout => {
		set({ lockout, challenge: null })
	},

	reset: () => {
		set({ mobileNumber: "", challenge: null, lockout: null })
	},
}))

export function panelEntryPhase(
	state: Pick<PanelEntryState, "challenge" | "lockout">,
): PanelEntryPhase {
	return state.challenge !== null || state.lockout !== null ? "code" : "number"
}
